/**
 * Which tested assets a question shows or lists, and where: the one
 * enumeration of a question's ties to the catalog, read from typed structure
 * only (refs, list items, tested-asset rows, a binding), never from wording.
 * Each reader decides which surfaces count for its own question: the coverage
 * rule (`testedAssetsShownToTwins`) keeps the ones the runner delivers a
 * stimulus by, and the applier's pre-exposure tie keeps the ones that make the
 * question need the material.
 *
 * Only catalog assets appear; a ref to an id the catalog does not hold is the
 * `asset_ref_missing` rule's, not a surface.
 */

type RecordLike = Record<string, any>;
const record = (value: unknown): RecordLike =>
  value && typeof value === 'object' && !Array.isArray(value) ? value : {};
const list = (value: unknown): Array<RecordLike> =>
  Array.isArray(value) ? value.filter(v => v && typeof v === 'object') : [];

/** The response lists whose items can be a tested asset, by their path. */
export type QuestionAssetItemList =
  | 'options'
  | 'categories'
  | 'rows.rows'
  | 'measures'
  | 'choiceAssetRefs';

export type QuestionAssetSurface =
  /** A question-level asset ref (`assetRefs[i]`), with its role. */
  | { kind: 'ref'; path: string; assetId: string; role: string | null }
  /** An answer, category, fixed row or measure that is an asset, by its refs. */
  | {
      kind: 'item';
      list: QuestionAssetItemList;
      path: string;
      assetId: string;
      role: string | null;
    }
  /** Grid rows that are the tested assets of a type (`rows.source`). */
  | { kind: 'tested_rows'; path: 'response.rows'; assetIds: Array<string> }
  /** A binding that shows every asset of its types, or each one in its lane. */
  | {
      kind: 'binding';
      path: 'assetBinding';
      mode: 'all_assets' | 'per_asset';
      assetIds: Array<string>;
    };

/** The catalog entries whose type is in `types`; every entry when none is named. */
function idsOfTypes(assets: Array<RecordLike>, types: unknown): Array<string> {
  return assets
    .filter(a => !Array.isArray(types) || !types.length || types.includes(a.type))
    .map(a => a.id);
}

const roleOf = (ref: RecordLike): string | null =>
  typeof ref.role === 'string' ? ref.role : null;

export function questionAssetSurfaces(
  question: unknown,
  testedAssets: unknown,
): Array<QuestionAssetSurface> {
  const q = record(question);
  const response = record(q.response);
  const binding = record(q.assetBinding);
  const assets = list(record(testedAssets).assets);
  const ids = new Set(assets.map(a => a.id).filter(id => typeof id === 'string'));
  const surfaces: Array<QuestionAssetSurface> = [];
  list(q.assetRefs).forEach((ref, index) => {
    if (ids.has(ref.assetId)) {
      surfaces.push({ kind: 'ref', path: `assetRefs[${index}]`, assetId: ref.assetId, role: roleOf(ref) });
    }
  });
  const rows = record(response.rows);
  const items: Array<[QuestionAssetItemList, string, Array<RecordLike>]> = [
    ['options', 'response.options', list(response.options)],
    ['categories', 'response.categories', list(response.categories)],
    ['rows.rows', 'response.rows.rows', list(rows.rows)],
    ['measures', 'response.measures', list(response.measures)],
    ['choiceAssetRefs', 'choiceAssetRefs', list(q.choiceAssetRefs)],
  ];
  items.forEach(([name, base, entries]) => entries.forEach((item, index) => {
    list(item.assetRefs).forEach(ref => {
      if (ids.has(ref.assetId)) {
        surfaces.push({ kind: 'item', list: name, path: `${base}[${index}]`, assetId: ref.assetId, role: roleOf(ref) });
      }
    });
  }));
  if (rows.source === 'tested_assets' || binding.rowSource === 'tested_assets') {
    surfaces.push({
      kind: 'tested_rows',
      path: 'response.rows',
      assetIds: idsOfTypes(assets, rows.assetType ? [rows.assetType] : binding.assetTypes),
    });
  }
  if (binding.mode === 'all_assets' || binding.mode === 'per_asset') {
    surfaces.push({
      kind: 'binding',
      path: 'assetBinding',
      mode: binding.mode,
      assetIds: idsOfTypes(assets, binding.assetTypes),
    });
  }
  return surfaces;
}
